import React from 'react';

import { TierSliderRow } from '../shared/TierSliderRow';

import type { TieredRates } from '../../../../../utils/rateExtractor';

interface SixTierSectionProps {
	creditRates: TieredRates;
	debitRates?: TieredRates;
	sameAsAbove: boolean;
	onSameAsAboveChange: (checked: boolean) => void;
	onDebitRateChange: (tier: keyof TieredRates, value: number) => void;
}

export const SixTierSection: React.FC<SixTierSectionProps> = ({
	creditRates,
	debitRates,
	sameAsAbove,
	onSameAsAboveChange,
	onDebitRateChange,
}) => {
	const displayRates = debitRates || creditRates;

	return (
		<div className="border-t border-gray-200 pt-6 space-y-6">
			<div className="flex items-center justify-between mb-4">
				<h5 className="text-md font-semibold text-gray-900">
					6-tier Pricing Model
				</h5>
				<div className="flex items-center">
					<input
						type="checkbox"
						id="sameAsAbove"
						checked={sameAsAbove}
						onChange={(e) => onSameAsAboveChange(e.target.checked)}
						className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
					/>
					<label htmlFor="sameAsAbove" className="ml-2 text-sm text-gray-700">
						Same as above
					</label>
				</div>
			</div>

			{/* Conditional rendering based on "Same as above" checkbox */}
			{sameAsAbove ? (
				/* Show read-only values when "Same as above" is checked */
				<div className="grid grid-cols-3 gap-6 items-center">
					<div className="text-center">
						<div className="flex items-center justify-center">
							<span className="text-yellow-600 mr-1">%</span>
							<span className="text-purple-600 font-medium">
								{creditRates.qualified_rate.toFixed(2)}
							</span>
						</div>
						<div className="text-xs text-gray-500 mt-1">Qualified</div>
						<div className="flex items-center justify-center mt-2">
							<span className="text-green-600 mr-1">$</span>
							<span className="text-purple-600 font-medium">
								{creditRates.qualified_per_item.toFixed(2)}
							</span>
						</div>
						<div className="text-xs text-gray-500">Authorization Fee</div>
					</div>
					<div className="text-center">
						<div className="flex items-center justify-center">
							<span className="text-yellow-600 mr-1">%</span>
							<span className="text-purple-600 font-medium">
								{creditRates.midqualified_rate.toFixed(2)}
							</span>
						</div>
						<div className="text-xs text-gray-500 mt-1">Mid-Qualified</div>
						<div className="flex items-center justify-center mt-2">
							<span className="text-green-600 mr-1">$</span>
							<span className="text-purple-600 font-medium">
								{creditRates.midqualified_per_item.toFixed(2)}
							</span>
						</div>
						<div className="text-xs text-gray-500">Authorization Fee</div>
					</div>
					<div className="text-center">
						<div className="flex items-center justify-center">
							<span className="text-yellow-600 mr-1">%</span>
							<span className="text-purple-600 font-medium">
								{creditRates.nonqualified_rate.toFixed(2)}
							</span>
						</div>
						<div className="text-xs text-gray-500 mt-1">Non-Qualified</div>
						<div className="flex items-center justify-center mt-2">
							<span className="text-green-600 mr-1">$</span>
							<span className="text-purple-600 font-medium">
								{creditRates.nonqualified_per_item.toFixed(2)}
							</span>
						</div>
						<div className="text-xs text-gray-500">Authorization Fee</div>
					</div>
				</div>
			) : (
				/* Show interactive sliders when "Same as above" is unchecked */
				<div className="space-y-6">
					{/* Qualified Row for 6-tier */}
					<TierSliderRow
						tierName="Qualified"
						rateValue={displayRates.qualified_rate}
						feeValue={displayRates.qualified_per_item}
						onRateChange={(value) => onDebitRateChange('qualified_rate', value)}
						onFeeChange={(value) =>
							onDebitRateChange('qualified_per_item', value)
						}
					/>

					{/* Mid-Qualified Row for 6-tier */}
					<TierSliderRow
						tierName="Mid-Qualified"
						rateValue={displayRates.midqualified_rate}
						feeValue={displayRates.midqualified_per_item}
						onRateChange={(value) =>
							onDebitRateChange('midqualified_rate', value)
						}
						onFeeChange={(value) =>
							onDebitRateChange('midqualified_per_item', value)
						}
					/>

					{/* Non-Qualified Row for 6-tier */}
					<TierSliderRow
						tierName="Non-Qualified"
						rateValue={displayRates.nonqualified_rate}
						feeValue={displayRates.nonqualified_per_item}
						onRateChange={(value) =>
							onDebitRateChange('nonqualified_rate', value)
						}
						onFeeChange={(value) =>
							onDebitRateChange('nonqualified_per_item', value)
						}
					/>
				</div>
			)}
		</div>
	);
};
